import { DestroyRef, inject, Inject, Injectable } from '@angular/core';
import { PLATFORM_API_URL } from '@core/constants';
import { BehaviorSubject, finalize, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { DocumentOCR } from '../model/document-ocr.model';
import { EventType, Origin, Routes, Target } from "@app/documents/models/document-ocr-types";
import { DocumentExt, IDocumentResponseDTO } from "../model";
import { DocumentsService } from "./document-utility.service";
import { DocumentsContextService } from './context/factory';
import { NotificationService } from './notification.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class OcrService {
  private ocrProcessingSubject = new BehaviorSubject<boolean>(false);
  ocrProcessing$ = this.ocrProcessingSubject.asObservable();
  
  private readonly destroyRef = inject(DestroyRef);
  
  constructor(
    private readonly http: HttpClient,
    @Inject(PLATFORM_API_URL) private readonly platformApiUrl: string,
    private documentService: DocumentsService,
    private notificationService: NotificationService
  ) {}

  sendOcrRequest(data: DocumentOCR): Observable<void> {
    return this.http.post<void>(`${this.platformApiUrl}/api/OCR/RequestScan`, data);
  }

  processDocumentToOcr(documentsContextService: DocumentsContextService, item: IDocumentResponseDTO, tenantId: string, authIdentifier: string, lineOfBusinessId: string, providerNPI: string, authorizationByProductId: string): void {
    this.ocrProcessingSubject.next(true); // Set processing state to true
    let file: DocumentExt;
    documentsContextService.getFileById(tenantId, authIdentifier, item.docMetaDataId)
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (res) => {
          file = res;
          if (file.fileContent) {
            const requestOCRPayload: DocumentOCR = this.createOCRRequestPayloadHH(file, item, lineOfBusinessId, providerNPI, authorizationByProductId);
            if (requestOCRPayload) {
              this.sendDataForOcrRequest(requestOCRPayload);
            }
        } 
      },
      error: () => {
        this.notificationService.showError(`The document ${file.documentFileName} is no longer available.`);
      }
    });
  }

  private sendDataForOcrRequest(ocrDocPayload: DocumentOCR): void {
    this.sendOcrRequest(ocrDocPayload).pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => {
        this.ocrProcessingSubject.next(false); // Reset processing state to false
      })
    ).subscribe({
      next: () => {
        this.notificationService.showSuccess('OASIS sent to OCR');
      },
      error: () => {
        this.notificationService.showError('An error occurred while sending OASIS to OCR.');
      }
    });
  }

  private createOCRRequestPayloadHH(file: DocumentExt, filedata: IDocumentResponseDTO, lineOfBusinessId: string, providerNPI: string, authorizationByProductId?: string): DocumentOCR {

    const typeTags = filedata?.documentType.split(',').map(item => item.trim());
    if(!typeTags.includes('OASIS')){
      typeTags.push('OASIS');
    }

    const ocrDocPayload: DocumentOCR = {
      id: null, 
      event: {
        type: EventType.CmdOcrRequest,
        datetime: new Date().toISOString(),
        origin: Origin.CarelonPacs,
        target: Target.CarelonOdna,
        correlationId: null,
        context: {
          authorizationId: filedata?.authorizationId,
          authorizationNumber: filedata?.authorizationNumber,
          authorizationByProductId: authorizationByProductId,
          lineOfBusinessId: lineOfBusinessId,
          productId: filedata?.productId,
          providerNPI: providerNPI ?? '9999999999',
          tenantId: filedata?.tenantId
        },
        requestRoute: Routes.RequestRoute,
        callbackRoute: Routes.CallbackRoute,
        document: {
          id: filedata.docMetaDataId,
          'mime-type': this.documentService.getContentType(file?.fileType), 
          size: this.documentService.getfileSize(file),            
          typeTags: typeTags,
          content: file?.fileContent,
          accessToken: null
        }
      }
    }

    return ocrDocPayload;
}

}
